import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';
import TrDateInput from '../TrDateInput';
import {
  FiyatlandirmaKurali, VadeAraligi, YONTEM_ORTALAMA_VADE, aralikEtiketi, araliklariDogrula,
} from '../../utils/fiyatlandirmaKontrol';

interface Props {
  projeID: number;
  onClose: () => void;
}

interface AralikSatir { bitis: string; oran: string; }

const YONTEMLER = [
  { value: YONTEM_ORTALAMA_VADE, label: 'Ortalama Vadeye Göre', aciklama: 'Teklif / sözleşmedeki ödeme planının ortalama vadesine göre liste fiyatından yapılabilecek en fazla indirimi sınırlar.' },
];

const sayi = (s: string) => (s.trim() === '' ? NaN : Number(s.replace(',', '.')));

const ProjeFiyatlandirmaModal: React.FC<Props> = ({ projeID, onClose }) => {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [aktifMi, setAktifMi] = useState(false);
  const [yontem, setYontem] = useState(YONTEM_ORTALAMA_VADE);
  const [maksVade, setMaksVade] = useState('');
  const [sonOdemeTarihi, setSonOdemeTarihi] = useState('');
  const [yoneticiKuralDisi, setYoneticiKuralDisi] = useState(false);
  const [satirlar, setSatirlar] = useState<AralikSatir[]>([]);
  const [yoneticiler, setYoneticiler] = useState<FiyatlandirmaKurali['yoneticiler']>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`/api/ProjeFiyatlandirma/${projeID}`);
        const k: FiyatlandirmaKurali = res.data;
        setAktifMi(k.aktifMi);
        setYontem(k.yontem || YONTEM_ORTALAMA_VADE);
        setMaksVade(k.maksOrtalamaVadeGun != null ? String(k.maksOrtalamaVadeGun) : '');
        setSonOdemeTarihi(k.sonOdemeTarihi || '');
        setYoneticiKuralDisi(k.yoneticiKuralDisiCikabilir);
        setSatirlar(k.araliklar.map(a => ({ bitis: String(a.bitisGun), oran: String(a.maksIndirimOrani) })));
        setYoneticiler(k.yoneticiler || []);
      } catch {
        toast.error('Fiyatlandırma ayarları yüklenemedi.');
        onClose();
      } finally {
        setYukleniyor(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeID]);

  // Başlangıç günleri kullanıcıdan alınmaz: ilk aralık 0'dan, her sonraki bir önceki bitiş + 1'den başlar.
  // Böylece aralıklar arasında boşluk ya da çakışma oluşamaz.
  const araliklar: VadeAraligi[] = useMemo(() => {
    const sonuc: VadeAraligi[] = [];
    satirlar.forEach((s, i) => {
      const baslangic = i === 0 ? 0 : sonuc[i - 1].bitisGun + 1;
      sonuc.push({ baslangicGun: baslangic, bitisGun: sayi(s.bitis), maksIndirimOrani: sayi(s.oran) });
    });
    return sonuc;
  }, [satirlar]);

  const maksVadeNum = maksVade.trim() === '' ? null : sayi(maksVade);
  const hatalar = araliklariDogrula(araliklar, maksVadeNum, aktifMi);

  const satirGuncelle = (i: number, patch: Partial<AralikSatir>) =>
    setSatirlar(prev => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const aralikEkle = () => setSatirlar(prev => [...prev, { bitis: '', oran: '' }]);

  const kalaniDoldur = () => {
    if (maksVadeNum == null || !Number.isFinite(maksVadeNum)) return;
    setSatirlar(prev => [...prev, { bitis: String(maksVadeNum), oran: '' }]);
  };

  const sonBitis = araliklar.length > 0 ? araliklar[araliklar.length - 1].bitisGun : -1;
  const kalanAralikVar = maksVadeNum != null && Number.isFinite(maksVadeNum) && Number.isFinite(sonBitis) && sonBitis < maksVadeNum;

  const handleKaydet = async () => {
    if (hatalar.length > 0) return;
    setKaydediliyor(true);
    try {
      await axiosInstance.post(`/api/ProjeFiyatlandirma/${projeID}`, {
        aktifMi,
        yontem,
        maksOrtalamaVadeGun: maksVadeNum,
        sonOdemeTarihi: sonOdemeTarihi || null,
        yoneticiKuralDisiCikabilir: yoneticiKuralDisi,
        araliklar,
      });
      toast.success('Fiyatlandırma ayarları kaydedildi.');
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Kaydedilirken hata oluştu.');
    } finally {
      setKaydediliyor(false);
    }
  };

  const lbl: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 700, color: '#475569', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const kart: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.5)' }} />
      <div onClick={e => e.stopPropagation()}
        style={{ position: 'relative', width: '96%', maxWidth: 820, maxHeight: '92vh', background: '#f8fafc', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 22px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16.5, fontWeight: 700, color: '#1a2440' }}>
              <i className="fas fa-tags" style={{ color: '#cd6906', marginRight: 8 }}></i>Fiyatlandırma
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Teklif ve sözleşmede satış fiyatının liste fiyatına göre sınırları</div>
          </div>
          <button type="button" onClick={onClose} style={{ width: 32, height: 32, border: 'none', borderRadius: '50%', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 17 }}>×</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {yukleniyor ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 30 }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Yükleniyor...</div>
          ) : (
            <>
              <label style={{ ...kart, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={aktifMi} onChange={e => setAktifMi(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#cd6906' }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a2440' }}>Fiyatlandırma Kontrolü</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Açıkken kurallara uymayan ödeme planlarında teklif ve sözleşme belgeleri yazdırılamaz.
                  </div>
                </div>
              </label>

              <div style={{ ...kart, opacity: aktifMi ? 1 : 0.6 }}>
                <div style={lbl}>Fiyatlandırma Yöntemi</div>
                {YONTEMLER.map(y => (
                  <label key={y.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', border: `1.5px solid ${yontem === y.value ? '#cd6906' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: yontem === y.value ? '#fff7ed' : '#fff' }}>
                    <input type="radio" name="fiyatlandirmaYontem" checked={yontem === y.value} onChange={() => setYontem(y.value)} style={{ marginTop: 3, accentColor: '#cd6906' }} />
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1a2440' }}>{y.label}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{y.aciklama}</div>
                    </div>
                  </label>
                ))}
              </div>

              {yontem === YONTEM_ORTALAMA_VADE && (
                <div style={{ ...kart, display: 'flex', flexDirection: 'column', gap: 14, opacity: aktifMi ? 1 : 0.6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={lbl}>En Fazla Ortalama Vade (gün)</label>
                      <input type="number" min={1} value={maksVade} onChange={e => setMaksVade(e.target.value)} placeholder="Örn. 120" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Son Ödeme Tarihi</label>
                      <TrDateInput value={sonOdemeTarihi} onChange={e => setSonOdemeTarihi(e.target.value)} style={inp} />
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Bu tarihten sonraya ödeme girilemez. Boş bırakılabilir.</div>
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={lbl}>Ortalama Vade Aralıkları ve En Fazla İndirim</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
                      Günler tam sayıdır ve aralığın iki ucu dahildir (örn. 0–40 ve 41–60). İlk aralık 0. günden başlar, her yeni aralık
                      bir öncekinin bittiği günün ertesinden başlar; son aralık en fazla ortalama vadede bitmelidir.
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['Başlangıç (gün)', 'Bitiş (gün)', 'En Fazla İndirim (%)', ''].map(h => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {satirlar.map((s, i) => (
                          <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 10px', width: 130 }}>
                              <div style={{ ...inp, background: '#f1f5f9', color: '#64748b' }}>
                                {Number.isFinite(araliklar[i].baslangicGun) ? araliklar[i].baslangicGun : '—'}
                              </div>
                            </td>
                            <td style={{ padding: '6px 10px', width: 150 }}>
                              <input type="number" min={0} value={s.bitis} onChange={e => satirGuncelle(i, { bitis: e.target.value })} style={inp} />
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              <input type="number" min={0} max={100} step="0.01" value={s.oran} onChange={e => satirGuncelle(i, { oran: e.target.value })} placeholder="Örn. 5" style={inp} />
                            </td>
                            <td style={{ padding: '6px 4px', width: 36, textAlign: 'center' }}>
                              <button type="button" title="Aralığı sil" onClick={() => setSatirlar(prev => prev.filter((_, idx) => idx !== i))}
                                style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 14 }}
                                onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')} onMouseLeave={e => (e.currentTarget.style.color = '#cbd5e1')}>
                                <i className="fas fa-times"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                      <button type="button" onClick={aralikEkle}
                        style={{ padding: '7px 13px', border: '1.5px dashed #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                        <i className="fas fa-plus" style={{ marginRight: 6, fontSize: 11 }}></i>Aralık Ekle
                      </button>
                      {kalanAralikVar && (
                        <button type="button" onClick={kalaniDoldur}
                          style={{ padding: '7px 13px', border: '1px solid #fdba74', borderRadius: 8, background: '#fff7ed', color: '#c2410c', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                          <i className="fas fa-arrow-right" style={{ marginRight: 6, fontSize: 11 }}></i>{sonBitis + 1}–{maksVadeNum} gün aralığını ekle
                        </button>
                      )}
                    </div>
                  </div>

                  {hatalar.length > 0 ? (
                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>Kaydetmeden önce tamamlanması gerekenler
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#92400e', lineHeight: 1.6 }}>
                        {hatalar.map(h => <li key={h}>{h}</li>)}
                      </ul>
                    </div>
                  ) : araliklar.length > 0 && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '10px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#166534', marginBottom: 4 }}>Özet</div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#166534', lineHeight: 1.6 }}>
                        {araliklar.map(a => (
                          <li key={a.baslangicGun}>Ortalama vade {aralikEtiketi(a)}: satış fiyatı liste fiyatının en fazla %{a.maksIndirimOrani} altında olabilir.</li>
                        ))}
                        {maksVadeNum != null && <li>Ortalama vade {maksVadeNum} günü aşan planlar kabul edilmez.</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <label style={{ ...kart, display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', opacity: aktifMi ? 1 : 0.6 }}>
                <input type="checkbox" checked={yoneticiKuralDisi} onChange={e => setYoneticiKuralDisi(e.target.checked)} style={{ width: 17, height: 17, marginTop: 2, accentColor: '#cd6906' }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1a2440' }}>Proje Yöneticisi Bu Kuralların Dışına Çıkabilir</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {yoneticiler.length > 0
                      ? <>Proje yöneticileri: <b>{yoneticiler.map(y => y.adSoyad).join(', ')}</b>. Kendi hesaplarıyla bu değerlerin dışında da plan girebilirler.</>
                      : 'Bu projede henüz proje yöneticisi yok. Proje Düzenle ekranından ekleyebilirsiniz.'}
                  </div>
                </div>
              </label>
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', background: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '9px 18px', border: '1px solid #e5e7eb', borderRadius: 9, background: '#fff', fontSize: 13.5, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>İptal</button>
          <button type="button" onClick={handleKaydet} disabled={yukleniyor || kaydediliyor || hatalar.length > 0}
            title={hatalar.length > 0 ? 'Önce uyarıdaki eksikleri tamamlayın' : undefined}
            style={{ padding: '9px 22px', border: 'none', borderRadius: 9, background: (yukleniyor || kaydediliyor || hatalar.length > 0) ? '#94a3b8' : '#cd6906', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: (yukleniyor || kaydediliyor || hatalar.length > 0) ? 'not-allowed' : 'pointer' }}>
            {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjeFiyatlandirmaModal;
